import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiClient, prepareApiRequest } from './client';
import { discoveryKeys } from './discovery';
import { newIdempotencyKey } from './engagement-requirements';
import { fileNameFromDisposition } from './requirement-spreadsheets';

/**
 * Requirements sign-off by the client (T-0191, over the T-0183 API;
 * E12-F11 §3). Everything is under
 * `/api/v1/engagements/:engagement_id/requirements`, with the Selection and
 * Discovery modules on (`not_found` otherwise).
 *
 *   - The checklist is computed on the server (`signoffChecklist` in
 *     `@caliber-2/discovery-core`), the same function the Overview card
 *     uses; the screen shows it and never counts.
 *   - Request, resend, cancel and waive take an `Idempotency-Key`, one per
 *     confirmed action. Who may do each is checked on the server.
 *   - There is no route that signs: only the signatory's secure link does.
 */

const ChecklistItemSchema = z.object({
  key: z.string(),
  ok: z.boolean(),
  label: z.string(),
  value: z.string(),
  target: z.object({ kind: z.string(), area_ids: z.array(z.string()).optional() }),
});
export type ChecklistItem = z.infer<typeof ChecklistItemSchema>;

const SIGNOFF_STATES = [
  'requested',
  'signed',
  'waived',
  'changes_requested',
  'reopened',
  'cancelled',
] as const;
export type SignoffState = (typeof SIGNOFF_STATES)[number];

const UserRefSchema = z.object({ id: z.string(), name: z.string() });

/** One sign-off of the engagement (a request or a waiver), with where it ended up. */
const HistoryItemSchema = z.object({
  signoff_id: z.string(),
  kind: z.enum(['request', 'waiver']),
  state: z.enum(SIGNOFF_STATES),
  at: z.string(),
  by: UserRefSchema.nullable(),
});
export type SignoffHistoryItem = z.infer<typeof HistoryItemSchema>;

export const SignoffStatusSchema = z.object({
  /** `none` until the first request or waiver. */
  state: z.enum([...SIGNOFF_STATES, 'none']),
  kind: z.enum(['request', 'waiver']).nullable(),
  signoff_id: z.string().nullable(),
  checklist: z.array(ChecklistItemSchema),
  checklist_complete: z.boolean(),
  /** The caller may request, resend, cancel and waive: the engagement lead or a firm admin. */
  can_lead: z.boolean(),
  signatory: z
    .object({
      person_id: z.string(),
      full_name: z.string(),
      title: z.string().nullable(),
      email: z.string().nullable(),
    })
    .nullable(),
  link: z
    .object({
      id: z.string(),
      expires_at: z.string(),
      opened_at: z.string().nullable(),
      status: z.string(),
    })
    .nullable(),
  snapshot: z
    .object({ id: z.string(), sequence_no: z.number(), captured_at: z.string() })
    .nullable(),
  waiver: z.object({ by: UserRefSchema, at: z.string(), reason: z.string() }).nullable(),
  signed: z.object({ name: z.string(), email: z.string(), at: z.string() }).nullable(),
  changes_comment: z.string().nullable(),
  /** What the reopen trigger recorded (E12-F11 §2); read with `reopenedChangesText`. */
  reopened_changes: z.unknown(),
  history: z.array(HistoryItemSchema),
  export_unlocked: z.boolean(),
});
export type SignoffStatus = z.infer<typeof SignoffStatusSchema>;

const base = (engagementId: string) =>
  `/api/v1/engagements/${encodeURIComponent(engagementId)}/requirements`;

export const getSignoff = (engagementId: string) =>
  apiClient(`${base(engagementId)}/signoff`, SignoffStatusSchema);

const write = <T>(engagementId: string, action: string, schema: z.ZodType<T>, body: unknown) =>
  apiClient(`${base(engagementId)}/signoff:${action}`, schema, {
    method: 'POST',
    body,
    idempotencyKey: newIdempotencyKey(),
  });

export const requestSignoff = (
  engagementId: string,
  body: { signatory_person_id: string; message: string },
) =>
  write(
    engagementId,
    'request',
    z
      .object({ signoff_id: z.string(), snapshot_id: z.string(), link_id: z.string() })
      .passthrough(),
    body,
  );

export const resendSignoff = (engagementId: string) =>
  write(engagementId, 'resend', z.object({ link_id: z.string() }).passthrough(), {});

export const cancelSignoff = (engagementId: string) =>
  write(engagementId, 'cancel', z.object({ state: z.string() }).passthrough(), {});

export const waiveSignoff = (engagementId: string, body: { reason: string }) =>
  write(
    engagementId,
    'waive',
    z.object({ signoff_id: z.string(), snapshot_id: z.string() }).passthrough(),
    body,
  );

/**
 * The vendor-facing packet PDF, with the signature or the waiver on its
 * cover. Refused (409 `signoff.export_locked`) unless the current sign-off
 * is signed or waived and nothing changed since (rule 26).
 */
export async function downloadPacket(
  engagementId: string,
): Promise<{ blob: Blob; fileName: string }> {
  const { url, headers, correlationId } = await prepareApiRequest(`${base(engagementId)}/packet`, {
    headers: { Accept: 'application/pdf' },
  });
  let res: Response;
  try {
    res = await fetch(url, { method: 'GET', headers });
  } catch (err) {
    throw new ApiError(
      0,
      'network',
      err instanceof Error ? err.message : 'Network error.',
      correlationId,
    );
  }
  if (!res.ok) {
    let body: { error?: { code?: string; message?: string; details?: Record<string, unknown> } };
    try {
      body = (await res.json()) as typeof body;
    } catch {
      body = {};
    }
    throw new ApiError(
      res.status,
      body.error?.code ?? 'internal',
      body.error?.message ?? `The packet could not be exported (status ${res.status}).`,
      correlationId,
      body.error?.details,
    );
  }
  return {
    blob: await res.blob(),
    fileName:
      fileNameFromDisposition(res.headers.get('content-disposition')) ?? 'requirements-packet.pdf',
  };
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const signoffKeys = {
  status: (engagementId: string) => ['requirements-signoff', engagementId] as const,
};

export function useSignoffQuery(engagementId: string, enabled = true) {
  return useQuery({
    queryKey: signoffKeys.status(engagementId),
    queryFn: () => getSignoff(engagementId),
    enabled,
    staleTime: 15_000,
    // Off modules answer not_found; retrying will not change that.
    retry: false,
  });
}

function useSignoffWrite<V, T>(engagementId: string, fn: (vars: V) => Promise<T>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: signoffKeys.status(engagementId) }),
        qc.invalidateQueries({ queryKey: discoveryKeys.summary(engagementId) }),
      ]),
  });
}

export const useRequestSignoffMutation = (engagementId: string) =>
  useSignoffWrite(engagementId, (body: { signatory_person_id: string; message: string }) =>
    requestSignoff(engagementId, body),
  );
export const useResendSignoffMutation = (engagementId: string) =>
  useSignoffWrite(engagementId, () => resendSignoff(engagementId));
export const useCancelSignoffMutation = (engagementId: string) =>
  useSignoffWrite(engagementId, () => cancelSignoff(engagementId));
export const useWaiveSignoffMutation = (engagementId: string) =>
  useSignoffWrite(engagementId, (body: { reason: string }) => waiveSignoff(engagementId, body));
